import React, { useReducer, useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Send, CheckCircle, AlertCircle } from 'lucide-react';

// Import our modular components
import { S } from '../styles.js';
import { initialStateDomestic, domesticReducer } from './state.js';
import { DOMESTIC_SECTION_KEYS } from '../../../constants.js';
import { validateSection, validateAll, saveFormProgress } from './utils.js';
import { Stepper } from './Stepper.jsx';
import { Button } from '../../SharedInputs.jsx';
import { createStateEnrollment } from "../../../services/EnrolmentService.js";
import { fileStorageService } from "../../../services/FileStorageService.js";

// Import section components
import { SectionPersonal } from './sections/SectionPersonal.jsx';
import { SectionCampusCourse } from './sections/SectionCampusCourse.jsx';
import { SectionIntake } from './sections/SectionIntake.jsx';
import { SectionVisa } from './sections/SectionVisa.jsx';
import { SectionLanguageCulture } from './sections/SectionLanguageCulture.jsx';
import { SectionDisability } from './sections/SectionDisability.jsx';
import { SectionSchoolingQualifications } from './sections/SectionSchoolingQualifications.jsx';
import { SectionOSHC } from './sections/SectionOSHC.jsx';
import { SectionEmploymentStudy } from './sections/SectionEmploymentStudy.jsx';
import { SectionAdditionalServices } from './sections/SectionAdditionalServices.jsx';
import { SectionRPLCredit } from './sections/SectionRPLCredit.jsx';
import { SectionUSI } from './sections/SectionUSI.jsx';
import { SectionDeclaration } from './sections/SectionDeclaration.jsx';

function generateReferenceId() {
  return crypto.randomUUID().split('-')[0].toUpperCase();
}

export default function DomesticEnrolmentForm() {
  // Form state management
  const [state, dispatch] = useReducer(domesticReducer, initialStateDomestic);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(null);

  // Track which sections have been "touched"
  const [touchedSections, setTouchedSections] = useState([]);

  // Auto-save form progress
  useEffect(() => {
    const timeoutId = setTimeout(() => saveFormProgress(state), 1000);
    return () => clearTimeout(timeoutId);
  }, [state]);

  // Update completed steps
  useEffect(() => {
    const newCompletedSteps = [];
    DOMESTIC_SECTION_KEYS.forEach((key, index) => {
      const validation = validateSection(key, state);
      if (validation.valid && index < currentStep) {
        newCompletedSteps.push(index);
      }
    });
    setCompletedSteps(newCompletedSteps);
  }, [state, currentStep]);

  const canGoPrev = currentStep > 0;
  const isLastStep = currentStep === DOMESTIC_SECTION_KEYS.length - 1;

  // Validate current section
  const currentValidation = validateSection(DOMESTIC_SECTION_KEYS[currentStep], state);
  const showErrors = touchedSections.includes(DOMESTIC_SECTION_KEYS[currentStep]);

  const nextStep = () => {
    // Mark section as touched
    setTouchedSections(prev => [...new Set([...prev, DOMESTIC_SECTION_KEYS[currentStep]])]);

    // Only go next if valid
    if (currentValidation.valid && !isLastStep) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => setCurrentStep(prev => prev - 1);

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < DOMESTIC_SECTION_KEYS.length) setCurrentStep(stepIndex);
  };

  const handleSubmit = async () => {
    // Mark all sections as touched before submit
    setTouchedSections([...DOMESTIC_SECTION_KEYS]);

    const validation = validateAll(state, DOMESTIC_SECTION_KEYS);
    if (!validation.valid) {
      setSubmitError("Please complete all required fields in each section before submitting.");
      const firstErrorSection = validation.problems[0];
      const stepIndex = DOMESTIC_SECTION_KEYS.indexOf(firstErrorSection.key);
      if (stepIndex >= 0) goToStep(stepIndex);
      return;
    }

    setSubmitError("");
    setSubmitting(true);

    try {
      const enrollmentId = generateReferenceId();
      const stateWithUrls = { ...state };

      // Passport file
      if (state.personal?.passportCopy) {
        const fileData = await fileStorageService.uploadFile(
          state.personal.passportCopy,
          `enrolment/${enrollmentId}/personal/${state.personal.passportCopy.name}`
        );
        stateWithUrls.personal.passportCopy = { url: fileData.url, path: fileData.path, name: state.personal.passportCopy.name };
      }

      // Visa files
      if (state.visa?.currentVisaCopy) {
        const fileData = await fileStorageService.uploadFile(
          state.visa.currentVisaCopy,
          `enrolment/${enrollmentId}/visa/${state.visa.currentVisaCopy.name}`
        );
        stateWithUrls.visa.currentVisaCopy = { url: fileData.url, path: fileData.path, name: state.visa.currentVisaCopy.name };
      }

      if (state.visa?.refusalLetter) {
        const fileData = await fileStorageService.uploadFile(
          state.visa.refusalLetter,
          `enrolment/${enrollmentId}/visa/${state.visa.refusalLetter.name}`
        );
        stateWithUrls.visa.refusalLetter = { url: fileData.url, path: fileData.path, name: state.visa.refusalLetter.name };
      }

      // Language file
      if (state.languageCulture?.currentEnglishCopy) {
        const fileData = await fileStorageService.uploadFile(
          state.languageCulture.currentEnglishCopy,
          `enrolment/${enrollmentId}/languageCulture/${state.languageCulture.currentEnglishCopy.name}`
        );
        stateWithUrls.languageCulture.currentEnglishCopy = { url: fileData.url, path: fileData.path, name: state.languageCulture.currentEnglishCopy.name };
      }

      // USI exemption
      if (state.usi?.usiExemptionLetter) {
        const fileData = await fileStorageService.uploadFile(
          state.usi.usiExemptionLetter,
          `enrolment/${enrollmentId}/usi/${state.usi.usiExemptionLetter.name}`
        );
        stateWithUrls.usi.usiExemptionLetter = { url: fileData.url, path: fileData.path, name: state.usi.usiExemptionLetter.name };
      }

      // Schooling transcripts (multiple files)
      if (state.schoolingQualifications?.transcripts?.length) {
        const filesData = await fileStorageService.uploadFiles(
          state.schoolingQualifications.transcripts,
          `enrolment/${enrollmentId}/transcripts`
        );
        stateWithUrls.schoolingQualifications.transcripts = filesData.map((f, i) => ({
          url: f.url,
          path: f.path,
          name: state.schoolingQualifications.transcripts[i].name
        }));
      }

      // RPL / Credit Evidence (multiple files)
      if (state.rplCredit?.creditEvidence?.length) {
        const filesData = await fileStorageService.uploadFiles(
          state.rplCredit.creditEvidence,
          `enrolment/${enrollmentId}/rplCreditEvidence`
        );
        stateWithUrls.rplCredit.creditEvidence = filesData.map((f, i) => ({
          url: f.url,
          path: f.path,
          name: state.rplCredit.creditEvidence[i].name
        }));
      }

      // Additional documents (multiple files)
      if (state.declaration?.additionalDocuments?.length) {
        const filesData = await fileStorageService.uploadFiles(
          state.declaration.additionalDocuments,
          `enrolment/${enrollmentId}/additionalDocuments`
        );
        stateWithUrls.declaration.additionalDocuments = filesData.map((f, i) => ({
          url: f.url,
          path: f.path,
          name: state.declaration.additionalDocuments[i].name
        }));
      }

      // Submit the updated state to Firestore
      const firestoreId = await createStateEnrollment(stateWithUrls, enrollmentId, "", "domestic");

      setSubmitting(false);
      setSubmitSuccess(enrollmentId);
    } catch (error) {
      setSubmitting(false);
      setSubmitError("Submission failed. Please try again or contact admissions support.");
    }
  };

  const renderCurrentSection = () => {
    const sectionKey = DOMESTIC_SECTION_KEYS[currentStep];
    const sectionData = state[sectionKey];
    const validation = validateSection(sectionKey, state);

    const commonProps = {
      value: sectionData,
      onChange: (payload) => dispatch({ type: "SET", section: sectionKey, payload }),
      errors: showErrors ? validation.errors : [],
      dispatch,
      category: "domestic"
    };

    switch (sectionKey) {
      case "personal":
        return <SectionPersonal {...commonProps} />;
      case "campusCourse":
        return <SectionCampusCourse {...commonProps} />;
      case "intake":
        return <SectionIntake {...commonProps} />;
      case "visa":
        return <SectionVisa {...commonProps} />;
      case "disability":
        return <SectionDisability {...commonProps} />;
      case "schoolingQualifications":
        return <SectionSchoolingQualifications {...commonProps} />;
      case "oshc":
        return <SectionOSHC {...commonProps} />;
      case "employmentStudyReason":
        return <SectionEmploymentStudy {...commonProps} />;
      case "additionalServices":
        return <SectionAdditionalServices {...commonProps} />;
      case "rplCredit":
        return <SectionRPLCredit {...commonProps} />;
      case "usi":
        return <SectionUSI {...commonProps} />;
      case "declaration":
        return <SectionDeclaration {...commonProps} />;
      default:
        return <div>Section not found</div>;
    }
  };

  if (submitSuccess) {
    return (
      <div style={S.page}>
        <motion.div
          style={S.card}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div style={{ textAlign: 'center', padding: 40 }}>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring" }}>
              <CheckCircle size={64} color="#10b981" style={{ marginBottom: 20 }} />
            </motion.div>

            <h1 style={{ ...S.h1, marginBottom: 16 }}>Application Submitted Successfully!</h1>
            <p style={{ fontSize: 18, color: '#6b7280', marginBottom: 24 }}>
              Thank you for your application to Canberra Institute of Education and Technology (CIET). We have received your enrollment form and will review it promptly.
            </p>

            <div style={S.successCard}>
              <CheckCircle size={20} color="#10b981" />
              <div>
                <strong>Reference ID: {submitSuccess}</strong>
                <p style={{ margin: '4px 0 0', fontSize: 14, color: '#6b7280' }}>
                  Please save this reference number for your records.
                </p>
              </div>
            </div>

            <Button variant="primary" onClick={() => window.location.reload()} style={{ marginTop: 24 }}>
              Submit Another Application
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <motion.div style={S.card} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', // aligns logo to top
            gap: 24 
          }}
        >
          {/* Header content */}
          <div>
            <h1 style={S.h1}>
              Application for Enrolment — Domestic Students
            </h1>
            <p style={{ ...S.hint, fontSize: 16, marginBottom: 24 }}>
              Complete all sections to submit your application to CIET. 
              Your progress is automatically saved as you fill out the form.
            </p>
          </div>

          {/* Logo */}
          <img
            style={{ height: '8rem', width: 'auto' }} // height matches h1
            src={require("../../../images/logo.png")}
            alt="CIET Logo"
          />
        </motion.div>
        <Stepper currentStep={currentStep} onStepClick={goToStep} formState={state} completedSteps={completedSteps} category="domestic"/>

       <motion.div
          style={{ position: "relative" }}
          animate={{ x: 0, opacity: 1 }}
          initial={null}        // <-- key change: prevents re-animation on re-render
          transition={{ duration: 0.3 }}
        >
          {renderCurrentSection()}
        </motion.div>


        <motion.div style={S.navigation} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div>
            {canGoPrev && <Button variant="secondary" onClick={prevStep} icon={ChevronLeft}>Previous</Button>}
          </div>

          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            {showErrors && !currentValidation.valid && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 14,
                  color: "#dc2626",
                  padding: "8px 12px",
                  background: "#fef2f2",
                  borderRadius: 8,
                  border: "1px solid #fecaca"
                }}
              >
                <AlertCircle size={16} />
                Please complete required fields
              </motion.div>
            )}
          </div>

          <div>
            {isLastStep ? (
              <Button variant="primary" onClick={handleSubmit} disabled={submitting} loading={submitting} icon={Send}>
                {submitting ? "Submitting..." : "Submit Application"}
              </Button>
            ) : (
              <Button variant="primary" onClick={nextStep} icon={ChevronRight}>Next Section</Button>
            )}
          </div>
        </motion.div>

        <AnimatePresence>
          {submitError && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} style={S.errorCard}>
              <AlertCircle size={20} color="#dc2626" />
              <div>
                <strong>Submission Error</strong>
                <p style={{ margin: "4px 0 0", fontSize: 14 }}>{submitError}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
      <div style={{height: '10rem'}}></div>
    </div>
  );
}
